// Overlay.jsx - Simplified version without marketing content
import { memo, useState, useEffect, useRef } from 'react';

import { BsArrowRight, BsArrowLeft } from 'react-icons/bs';
import { FcGoogle } from 'react-icons/fc';
import TypingText from './TypingText';
import { useAuth } from '../auth/AuthContext';
import LoadingDots from './LoadingDots';
import LoadingDiv from './LoadingDiv';

const Overlay = memo(({ windowWidth, assetsLoading, showLoginView, smallerThan850, isSmallSize, navigateBack, showCreateAccount, handleStartApp, toggleLoginView, signInWithGoogle, token, toggleCreateAccountView, setShowPrivacyPolicyDialog, isMobile }) => {
  const { signInWithEmail, register, resetPassword, error, deviceIssues } = useAuth();
  
  // Enhanced responsive breakpoints
  const isVerySmall = windowWidth < 480;
  const isSmall = windowWidth < 768;
  const isMedium = windowWidth >= 768 && windowWidth < 1024;
  const isLarge = windowWidth >= 1024;
  
  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);

  // Auto-populate invite code from URL ?invite=CODE
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('invite');
    if (code) {
      setInviteCode(code.toUpperCase());
    }
  }, []);
  
  // Hamburger menu state
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuItemClick = (action) => {
    setIsMenuOpen(false);
    action();
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Form handling functions
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFormError('');
    
    try {
      const result = await signInWithEmail(email, password);
      
      if (result.success) {
        // If email isn't verified, show the verification message within login UI
        if (result.emailVerified === false) {
          setShowVerificationMessage(true);
        }
        // Auth state will update UI; no full reload here
      } else {
        // Handle verification needed
        if (result.error === "Email not verified") {
          setShowVerificationMessage(true);
        } else {
          setFormError(result.error || 'Login failed');
        }
      }
    } catch (error) {
      setFormError(error.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegistration = async (e) => {
    e.preventDefault();
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setFormError('Please enter a valid email address');
      return;
    }
    
    if (password.length < 6) {
      setFormError('Password must be at least 6 characters long');
      return;
    }
    
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    setFormError('');
    
    try {
      const result = await register(email, password, inviteCode);
      
      if (result.success) {
        setSuccessMessage(result.message || 'Registration successful! Please check your email to verify your account.');
        // Clear form
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setInviteCode('');
      } else {
        setFormError(result.error || 'Registration failed');
      }
    } catch (error) {
      setFormError(error.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setFormError('Please enter a valid email address');
      return;
    }
    
    setIsLoading(true);
    setFormError('');
    
    try {
      const result = await resetPassword(email);
      
      if (result.success) {
        setSuccessMessage('Password reset email sent! Please check your inbox.');
        setShowResetForm(false);
      } else {
        setFormError(result.error || 'Failed to send reset email');
      }
    } catch (error) {
      setFormError(error.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form state when switching views
  useEffect(() => {
    setFormError('');
    setSuccessMessage('');
    setShowResetForm(false);
    setShowVerificationMessage(false);
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    // Re-check URL for invite code when switching to create account view
    const params = new URLSearchParams(window.location.search);
    const code = params.get('invite');
    if (code) setInviteCode(code.toUpperCase());
    else setInviteCode('');
  }, [showLoginView, showCreateAccount]);

  return (
    <div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="introduction-panel"
      transition={{ duration: 0.6 }}
      exit={{ opacity: 0, transition: { duration: 0 } }}
      style={{
        position: "relative",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: `linear-gradient(to right, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 1) 50%, rgba(0, 0, 0, 1) 70%)`,
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        boxSizing: "border-box",
        minHeight: "100vh",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: "center",
        zIndex: 99,
        overflow: "hidden"
      }}
    >
      {/* Main video background */}
      <div style={{ 
          display: "flex", 
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          position: isMobile ? "absolute" : "relative",
          left: 0,
          top: 0,
          width: isMobile ? "100%" : "50%",
          height: isMobile ? "100%" : "auto",
          zIndex: isMobile ? 1 : 99,
      }}>
        <div style={{
          position: "relative",
          display: "flex", 
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          width: "100%",
          height: isMobile ? "100%" : "auto",
        }}>
          <video
            style={{
              width: isMobile ? "100%" : "720px",
              height: isMobile ? "100%" : "auto",
              maxWidth: isMobile ? "100%" : "720px",
              objectFit: isMobile ? "cover" : "contain",
              zIndex: 99,
              opacity: showCreateAccount || showLoginView ? 0.7 : 1
            }}
            autoPlay={true}
            loop
            muted
            playsInline
          >
            <source src="floating-psychological-self.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>

      {/* Warp effect image */}
      <img
        src="warp-effect.png"
        alt="Overlay"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 1,
          opacity: showCreateAccount || showLoginView ? 0.7 : 1
        }}
      />

      {/* Top right hamburger menu */}
      {!showLoginView && !showCreateAccount && (
        <div 
          ref={menuRef}
          style={{
            position: "absolute",
            top: isVerySmall ? "10px" : isMobile ? "15px" : "20px",
            right: isVerySmall ? "10px" : isMobile ? "15px" : "20px",
            zIndex: 100,
          }}
        >
          {/* Hamburger button */}
          <button
            onClick={toggleMenu}
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              borderRadius: "8px",
              padding: "8px",
              cursor: "pointer",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              width: "40px",
              height: "40px",
              transition: "all 0.3s ease"
            }}
          >
            {/* Hamburger lines */}
            <div style={{
              width: "20px",
              height: "2px",
              backgroundColor: "white",
              transition: "all 0.3s ease",
              transform: isMenuOpen ? "rotate(45deg) translate(5px, 5px)" : "none",
              marginBottom: isMenuOpen ? "0" : "3px"
            }} />
            <div style={{
              width: "20px",
              height: "2px",
              backgroundColor: "white",
              transition: "all 0.3s ease",
              opacity: isMenuOpen ? "0" : "1",
              marginBottom: isMenuOpen ? "0" : "3px"
            }} />
            <div style={{
              width: "20px",
              height: "2px",
              backgroundColor: "white",
              transition: "all 0.3s ease",
              transform: isMenuOpen ? "rotate(-45deg) translate(7px, -6px)" : "none"
            }} />
          </button>

          {/* Dropdown menu */}
          {isMenuOpen && (
            <div style={{
              position: "absolute",
              top: "50px",
              right: "0",
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              borderRadius: "12px",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              padding: "8px",
              minWidth: "160px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
              animation: "fadeInDown 0.3s ease"
            }}>
              <button
                onClick={() => handleMenuItemClick(toggleLoginView)}
                className="hamburger-menu-item"
                style={{
                  width: "100%",
                  backgroundColor: "transparent",
                  color: "white",
                  border: "none",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  textAlign: "left",
                  transition: "all 0.2s ease",
                  marginBottom: "4px"
                }}
              >
                Sign In
              </button>
              <button
                onClick={() => handleMenuItemClick(toggleCreateAccountView)}
                className="hamburger-menu-item"
                style={{
                  width: "100%",
                  backgroundColor: "transparent",
                  color: "white",
                  border: "none",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  textAlign: "left",
                  transition: "all 0.2s ease"
                }}
              >
                Create Account
              </button>
            </div>
          )}
        </div>
      )}

      {/* Main content area */}
      <div style={{
        position: isMobile ? "relative" : "absolute",
        right: isMobile ? "auto" : 0,
        top: isMobile ? "auto" : 0,
        width: isMobile ? "100%" : "50%",
        height: isMobile ? "auto" : "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 10,
        padding: isVerySmall ? "60px 15px 30px" : isMobile ? "80px 20px 40px" : "20px",
        boxSizing: "border-box",
        backgroundColor: isMobile ? "rgba(0, 0, 0, 0.8)" : "transparent",
        backdropFilter: isMobile ? "blur(10px)" : "none",
        WebkitBackdropFilter: isMobile ? "blur(10px)" : "none"
      }}>
        {/* Welcome section */}
        {!showLoginView && !showCreateAccount && (
          <div style={{
            textAlign: "center",
            color: "white",
            maxWidth: "400px",
            width: "100%"
          }}>
            <h1 style={{
              fontSize: isVerySmall ? "1.5rem" : isMobile ? "1.8rem" : "3rem",
              marginBottom: "1rem",
              background: "linear-gradient(to right, #bd7b69, #3b725f)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              lineHeight: isMobile ? "1.2" : "1.1"
            }}>
              Welcome to Heart Felt Souls
            </h1>
            <p style={{
              fontSize: isVerySmall ? "0.9rem" : isMobile ? "1rem" : "1.1rem",
              marginBottom: "2rem",
              opacity: 0.9,
              lineHeight: "1.6",
              padding: isMobile ? "0 10px" : "0"
            }}>
              Your AI companion for meaningful conversations and self-discovery.
            </p>
            <button
              onClick={token ? handleStartApp : toggleCreateAccountView}
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                color: "#000",
                border: "none",
                padding: isVerySmall ? "0.6rem 1.2rem" : isMobile ? "0.75rem 1.5rem" : "0.8rem 2rem",
                borderRadius: "2rem",
                fontSize: isVerySmall ? "13px" : isMobile ? "14px" : "16px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                margin: "0 auto",
                transition: "all 0.2s ease",
                minWidth: "fit-content"
              }}
            >
              <span>{token ? "Enter App" : "Get Started"}</span>
              <BsArrowRight style={{ marginLeft: "0.5rem" }} />
            </button>
          </div>
        )}

        {/* Login Form */}
        {showLoginView && (
          <div style={{
            width: "100%",
            maxWidth: isMobile ? "100%" : "400px",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            borderRadius: "1rem",
            padding: isMobile ? "1.5rem" : "2rem",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            margin: isMobile ? "0 10px" : "0"
          }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "1.5rem" }}>
              <button
                onClick={navigateBack}
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  color: "white",
                  cursor: "pointer",
                  marginRight: "1rem",
                  padding: "0.5rem"
                }}
              >
                <BsArrowLeft size={20} />
              </button>
              <h2 style={{ color: "white", margin: 0, fontSize: isMobile ? "1.3rem" : "1.5rem" }}>Sign In</h2>
            </div>

            {showVerificationMessage && (
              <div style={{
                backgroundColor: "rgba(255, 193, 7, 0.1)",
                border: "1px solid rgba(255, 193, 7, 0.3)",
                borderRadius: "0.5rem",
                padding: isMobile ? "0.8rem" : "1rem",
                marginBottom: "1rem",
                color: "#ffc107",
                fontSize: isMobile ? "0.9rem" : "1rem"
              }}>
                Please check your email and click the verification link before signing in.
              </div>
            )}

            {/* Device compatibility warnings */}
            {deviceIssues && deviceIssues.length > 0 && (
              <div style={{
                backgroundColor: "rgba(255, 152, 0, 0.1)",
                border: "1px solid rgba(255, 152, 0, 0.3)",
                borderRadius: "0.5rem",
                padding: isMobile ? "0.8rem" : "1rem",
                marginBottom: "1rem",
                color: "#ff9800",
                fontSize: isMobile ? "0.9rem" : "1rem"
              }}>
                <div style={{ fontWeight: "bold", marginBottom: "0.5rem" }}>
                  Device Compatibility Issues Detected:
                </div>
                <ul style={{ margin: "0", paddingLeft: "1rem" }}>
                  {deviceIssues.map((issue, index) => (
                    <li key={index} style={{ marginBottom: "0.25rem" }}>
                      {issue}
                    </li>
                  ))}
                </ul>
                <div style={{ fontSize: "0.8rem", marginTop: "0.5rem", opacity: 0.9 }}>
                  These issues may prevent login from working properly on this device.
                </div>
              </div>
            )}

            {/* Auth errors */}
            {error && (
              <div style={{
                backgroundColor: "rgba(220, 53, 69, 0.1)",
                border: "1px solid rgba(220, 53, 69, 0.3)",
                borderRadius: "0.5rem",
                padding: isMobile ? "0.8rem" : "1rem",
                marginBottom: "1rem",
                color: "#dc3545",
                fontSize: isMobile ? "0.9rem" : "1rem"
              }}>
                {error}
              </div>
            )}

            {formError && (
              <div style={{
                backgroundColor: "rgba(220, 53, 69, 0.1)",
                border: "1px solid rgba(220, 53, 69, 0.3)",
                borderRadius: "0.5rem",
                padding: isMobile ? "0.8rem" : "1rem",
                marginBottom: "1rem",
                color: "#dc3545",
                fontSize: isMobile ? "0.9rem" : "1rem"
              }}>
                {formError}
              </div>
            )}

            {successMessage && (
              <div style={{
                backgroundColor: "rgba(25, 135, 84, 0.1)",
                border: "1px solid rgba(25, 135, 84, 0.3)",
                borderRadius: "0.5rem",
                padding: isMobile ? "0.8rem" : "1rem",
                marginBottom: "1rem",
                color: "#198754",
                fontSize: isMobile ? "0.9rem" : "1rem"
              }}>
                {successMessage}
              </div>
            )}

            <form onSubmit={showResetForm ? handlePasswordReset : handleEmailLogin}>
              <div style={{ marginBottom: "1rem" }}>
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{
                    width: "100%",
                    padding: isMobile ? "0.8rem" : "0.75rem",
                    borderRadius: "0.5rem",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    color: "white",
                    fontSize: isMobile ? "16px" : "1rem", // 16px prevents zoom on iOS
                    boxSizing: "border-box"
                  }}
                  required
                />
              </div>

              {!showResetForm && (
                <div style={{ marginBottom: "1rem" }}>
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{
                      width: "100%",
                      padding: isMobile ? "0.8rem" : "0.75rem",
                      borderRadius: "0.5rem",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      color: "white",
                      fontSize: isMobile ? "16px" : "1rem", // 16px prevents zoom on iOS
                      boxSizing: "border-box"
                    }}
                    required
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                style={{
                  width: "100%",
                  padding: isMobile ? "0.8rem" : "0.75rem",
                  borderRadius: "0.5rem",
                  border: "none",
                  backgroundColor: isLoading ? "#6c757d" : "#007bff",
                  color: "white",
                  fontSize: isMobile ? "16px" : "1rem",
                  fontWeight: "600",
                  cursor: isLoading ? "not-allowed" : "pointer",
                  marginBottom: "1rem"
                }}
              >
                {isLoading ? <LoadingDots size={4} /> : (showResetForm ? "Send Reset Email" : "Sign In")}
              </button>
            </form>

            <div style={{ textAlign: "center", marginBottom: "1rem" }}>
              <button
                onClick={() => setShowResetForm(!showResetForm)}
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  color: "#007bff",
                  cursor: "pointer",
                  textDecoration: "underline",
                  fontSize: isMobile ? "0.85rem" : "0.9rem"
                }}
              >
                {showResetForm ? "Back to Sign In" : "Forgot password?"}
              </button>
            </div>

            <div style={{ textAlign: "center" }}>
              <button
                onClick={signInWithGoogle}
                style={{
                  width: "100%",
                  padding: isMobile ? "0.8rem" : "0.75rem",
                  borderRadius: "0.5rem",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  color: "white",
                  fontSize: isMobile ? "16px" : "1rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem"
                }}
              >
                <FcGoogle size={20} />
                Sign in with Google
              </button>
            </div>
          </div>
        )}

        {/* Registration Form */}
        {showCreateAccount && (
          <div style={{
            width: "100%",
            maxWidth: isMobile ? "100%" : "400px",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            borderRadius: "1rem",
            padding: isMobile ? "1.5rem" : "2rem",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            margin: isMobile ? "0 10px" : "0"
          }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "1.5rem" }}>
              <button
                onClick={navigateBack}
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  color: "white",
                  cursor: "pointer",
                  marginRight: "1rem",
                  padding: "0.5rem"
                }}
              >
                <BsArrowLeft size={20} />
              </button>
              <h2 style={{ color: "white", margin: 0, fontSize: isMobile ? "1.3rem" : "1.5rem" }}>Create Account</h2>
            </div>

            {formError && (
              <div style={{
                backgroundColor: "rgba(220, 53, 69, 0.1)",
                border: "1px solid rgba(220, 53, 69, 0.3)",
                borderRadius: "0.5rem",
                padding: isMobile ? "0.8rem" : "1rem",
                marginBottom: "1rem",
                color: "#dc3545",
                fontSize: isMobile ? "0.9rem" : "1rem"
              }}>
                {formError}
              </div>
            )}

            {successMessage && (
              <div style={{
                backgroundColor: "rgba(25, 135, 84, 0.1)",
                border: "1px solid rgba(25, 135, 84, 0.3)",
                borderRadius: "0.5rem",
                padding: isMobile ? "0.8rem" : "1rem",
                marginBottom: "1rem",
                color: "#198754",
                fontSize: isMobile ? "0.9rem" : "1rem"
              }}>
                {successMessage}
              </div>
            )}

            <form onSubmit={handleRegistration}>
              <div style={{ marginBottom: "1rem" }}>
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{
                    width: "100%",
                    padding: isMobile ? "0.8rem" : "0.75rem",
                    borderRadius: "0.5rem",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    color: "white",
                    fontSize: isMobile ? "16px" : "1rem", // 16px prevents zoom on iOS
                    boxSizing: "border-box"
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <input
                  type="password"
                  placeholder="Password (min 6 characters)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{
                    width: "100%",
                    padding: isMobile ? "0.8rem" : "0.75rem",
                    borderRadius: "0.5rem",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    color: "white",
                    fontSize: isMobile ? "16px" : "1rem", // 16px prevents zoom on iOS
                    boxSizing: "border-box"
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <input
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{
                    width: "100%",
                    padding: isMobile ? "0.8rem" : "0.75rem",
                    borderRadius: "0.5rem",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    color: "white",
                    fontSize: isMobile ? "16px" : "1rem", // 16px prevents zoom on iOS
                    boxSizing: "border-box"
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <input
                  type="text"
                  placeholder="Invite Code"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  style={{
                    width: "100%",
                    padding: isMobile ? "0.8rem" : "0.75rem",
                    borderRadius: "0.5rem",
                    border: inviteCode ? "1px solid rgba(232, 199, 123, 0.4)" : "1px solid rgba(255, 255, 255, 0.2)",
                    backgroundColor: inviteCode ? "rgba(232, 199, 123, 0.08)" : "rgba(255, 255, 255, 0.05)",
                    color: "white",
                    fontSize: isMobile ? "16px" : "1rem",
                    boxSizing: "border-box",
                    letterSpacing: "2px",
                    fontFamily: "monospace",
                    textAlign: "center",
                  }}
                  required
                />
                <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.4)", marginTop: "4px", textAlign: "center" }}>
                  An invite code is required to create an account
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || !inviteCode}
                style={{
                  width: "100%",
                  padding: isMobile ? "0.8rem" : "0.75rem",
                  borderRadius: "0.5rem",
                  border: "none",
                  backgroundColor: isLoading ? "#6c757d" : "#28a745",
                  color: "white",
                  fontSize: isMobile ? "16px" : "1rem",
                  fontWeight: "600",
                  cursor: isLoading ? "not-allowed" : "pointer",
                  marginBottom: "1rem"
                }}
              >
                {isLoading ? <LoadingDots size={4} /> : "Create Account"}
              </button>
            </form>

            <div style={{ textAlign: "center", marginBottom: "1rem" }}>
              <button
                onClick={signInWithGoogle}
                style={{
                  width: "100%",
                  padding: isMobile ? "0.8rem" : "0.75rem",
                  borderRadius: "0.5rem",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  color: "white",
                  fontSize: isMobile ? "16px" : "1rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem"
                }}
              >
                <FcGoogle size={20} />
                Sign up with Google
              </button>
            </div>

            <div style={{ textAlign: "center", fontSize: "0.9rem", color: "rgba(255, 255, 255, 0.7)" }}>
              By creating an account, you agree to our{" "}
              <button
                onClick={() => setShowPrivacyPolicyDialog(true)}
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  color: "#007bff",
                  cursor: "pointer",
                  textDecoration: "underline",
                  padding: 0,
                  fontSize: "inherit"
                }}
              >
                Privacy Policy
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Loading overlay */}
      {assetsLoading && (
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 999999999
        }}>
          <LoadingDiv
            isLoading 
            duration={0.75} 
            width={`${30}px`}
            height={`${30}px`}
            borderWidth={1}
            loadingColor="#FFFFFF"
            borderColor="rgba(255, 255, 255, 0.5)"
            borderRadius={`${10}px`}
            backgroundColor="transparent"
            loadingSegmentPercentage={25}
          />
        </div>
      )}
    </div>
  );
});

export default Overlay;
